import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation} from "@angular/core";
import {Base} from "../../../../../../common/interfaces/base.interfaces";
import {Modal, ModalService} from "src/modules/section/services/modal.service";
import {Api} from "src/modules/common/services/api.service";
import {Api3Service} from "src/modules/common/services/api3.service";
import {StorageService} from "src/modules/common/services/storage.service";
import {UserService} from "src/modules/common/services/user.service";
import * as moment from "moment-timezone";
import {LogsFilter, LogsFilterComponent} from "../filter/filter.component";
import {SpinnerService} from "../../../../../../common/services/spinner.service";
import {IPagination, IPaginationEvent} from "src/modules/common/components/pagination/pagination.component";


@Component({
    selector: "admin-logs-list",
    templateUrl: "list.component.html",
    styleUrls: [
        "list.component.scss"
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class LogsListComponent implements Base.IComponent, OnInit {

    public readonly state: Base.IState;

    public filterState: LogsFilter.IState = {date_range: null, level: null, rules: []};

    public logs: any[] = [];

    public pagination: IPagination<any>;

    public page: number = 1;

    public perPage: number = 50;

    /**
     * Rows currently expanded to show their full detail below (by row reference).
     */
    public expanded: Set<any> = new Set<any>();

    public constructor(
        private modalService: ModalService,
        private api3Service: Api3Service,
        private storageService: StorageService,
        private userService: UserService,
        private changeDetectorRef: ChangeDetectorRef,
        private spinnerService: SpinnerService
    ) {
    }

    /**
     * Number of active filters (date range + level + advanced rules), shown on the badge.
     */
    public get filterCount(): number {
        return (this.filterState.date_range ? 1 : 0)
            + (this.filterState.level ? 1 : 0)
            + this.filterState.rules.length;
    }

    private get query(): { [key: string]: any } {
        return {
            date_range: this.filterState.date_range,
            level: this.filterState.level,
            filters: this.filterState.rules.length ? JSON.stringify(this.filterState.rules) : null,
            page: this.page,
            per_page: this.perPage
        };
    }

    public time(row: any): string {
        return row.timestamp
            ? moment.utc(row.timestamp).tz("Asia/Jerusalem").format("YYYY-MM-DD HH:mm:ss")
            : "";
    }

    public levelStyle(level: string): { [key: string]: string } {
        switch ((level || "").toUpperCase()) {
            case "ERROR":
            case "CRITICAL":
            case "ALERT":
            case "EMERGENCY":
                return {color: "#c62828", "font-weight": "600"};
            case "WARNING":
                return {color: "#ef6c00", "font-weight": "600"};
            case "NOTICE":
            case "INFO":
                return {color: "#2e7d32"};
            default:
                return {color: "#757575"};
        }
    }

    public toggle(row: any): void {
        if (this.expanded.has(row)) {
            this.expanded.delete(row);
        } else {
            this.expanded.add(row);
        }
        this.changeDetectorRef.markForCheck();
    }

    /**
     * Fetch the current page of logs and hand the paginator payload to
     * common-pagination.
     */
    private async load(): Promise<void> {
        this.spinnerService.show();

        const {data}: Api.IResponse = await this.api3Service
            .get(`/admin/logs`, this.query);

        this.spinnerService.hide();

        this.pagination = data ?? null;
        this.logs = data?.data ?? [];
        this.page = data?.current_page ?? this.page;

        this.changeDetectorRef.markForCheck();
    }

    public reload(): void {
        this.page = 1;
        this.expanded.clear();
        this.load();
    }

    /**
     * Page / per-page change from common-pagination.
     */
    public onPage(event: IPaginationEvent): void {
        this.page = event.page;
        this.perPage = event.per_page;
        this.expanded.clear();
        this.load();
    }

    public async openFilters(): Promise<void> {
        const response: Modal.IResponse = await this.modalService.open(LogsFilterComponent, {
            state: this.filterState,
            modalWidth: 800
        });

        if (response && response.name === "save") {
            this.filterState = LogsFilter.toState(response.value);
            this.reload();
        }
    }

    public ngOnInit(): void {
        this.filterState = LogsFilter.toState(
            this.storageService.get(LogsFilter.storageKey(this.userService.data?.id))
        );

        this.load();
    }

    public ngConfig(): Base.IConfig {
        return {
            name: "logs",
            actions: {
                "browse": "*"
            }
        };
    }
}
